import React, { useContext, useEffect, useState } from 'react';
import Logo from "../../../../../assets/images/LogoNew.svg";
import { ApplicantHeaderActions } from '../../../../../components/ApplicationInvite';
import LanguagePicker from '../../../../../components/LanguagePicker/LanguagePicker';
import LogoutWithReason from '../../../../../components/LogoutWithReason/LogoutWithReason';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import { doGET } from '../../../../../util/HttpUtil';
// import RedirectToBusinessPrime from '../../BusinessLiteForms/RedirectToBusinessPrime';
import BusinessPlusKYCStatus from './BusinessPlusKYCStatus';
import JourneySteps from './JourneySteps';
import { FORM_TYPE, FORM_TYPE_LABELS, STEPS, getApplicationTitle } from '../../IPlushelper';
import { useNavigate } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa';
import { buildAutomationChildTestId, resolveAutomationTestId } from '../../../../../components/InputField/testId';
import { getBrokerProfile } from '../../../../Broker/BrokerService';


const ThankYou = ({
    application_id,
    type,
    applicationType,
    isForEdd,
    isKycRequired = true,
    requiredUboIds,
    applicationForm,
    formData,
    testId,
}) => {
    const { t } = useTranslation()

    const { showError } = useContext(DialogContext);
    const navigate = useNavigate()

    const [loading, setLoading] = useState(true);
    const [isAppliedForPrime, setIsAppliedForPrime] = useState(false);
    const [isPrimeApplicationSubmitted, setIsPrimeApplicationSubmitted] = useState(false);
    const [allKYCComplete, setAllKYCComplete] = useState(false);
    const [inviteApplicationForm, setInviteApplicationForm] = useState(applicationForm || {});
    const effectiveKycRequired = isKycRequired && !(type === FORM_TYPE.Business && Number(applicationType) === 3);
    const resolvedTestId = resolveAutomationTestId(testId, "individual-plus-thank-you");
    const isBrokerSession = getBrokerProfile()?.role === "broker_admin";

    useEffect(() => {
        if (applicationForm) {
            setInviteApplicationForm(applicationForm);
        }
    }, [applicationForm]);

    // Check if the user already has a BP application
    const checkIfUpgradeToPrime = async () => {
        setLoading(true);
        try {
            const response = await doGET(`/api/application-form/grid?plus_id=${application_id}`);
            if (response?.status === 200 && response?.data?.rows?.length) {
                setIsAppliedForPrime(true);
                const applicationFormStep = response?.data?.rows?.[0]?.steps?.find(v => v?.label == STEPS.APPLICATION_FORM)
                const documentStep = response?.data?.rows?.[0]?.steps?.find(v => v?.label == STEPS.DOCUMENT_VERIFICATION)
                if ([0, 2]?.includes(applicationFormStep?.status) || [0, 2]?.includes(documentStep?.status)) {
                    navigate(`/business-prime/${response?.data?.rows?.[0]?._id}`)
                } else if (![0, 2]?.includes(applicationFormStep?.status)) {
                    setIsPrimeApplicationSubmitted(true)
                }
            }
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (application_id && [FORM_TYPE.BusinessPlus, FORM_TYPE.BusinessPrime, FORM_TYPE.Business].includes(type)) {
            checkIfUpgradeToPrime();
        } else {
            setLoading(false);
        }
    }, [application_id]);

    if (loading) {
        return <div style={{ height: "100vh" }} className=" d-flex justify-content-center align-items-center flex-1" data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}>
            <FaSpinner size={20} className="spinner" />
        </div>
    }

    return (
        <div className="form-content-container xl" data-testid={resolvedTestId} data-record-id={application_id}>
            <div className='form-content-header' data-testid={buildAutomationChildTestId(resolvedTestId, "header")}>
                {!isBrokerSession ? (
                    <img
                        src={Logo}
                        alt={t("fv-bank")}
                        className="logo logo-dark form-content-logo"
                        data-testid={buildAutomationChildTestId(resolvedTestId, "logo")}
                    />
                ) : null}
                <div className='d-flex align-items-center gap-3' data-testid={buildAutomationChildTestId(resolvedTestId, "header-actions")}>
                    <div className='' data-testid={buildAutomationChildTestId(resolvedTestId, "language-wrap")}><LanguagePicker testId={buildAutomationChildTestId(resolvedTestId, "language")} /></div>
                    <ApplicantHeaderActions
                        applicationForm={inviteApplicationForm}
                        formData={formData}
                        formId={application_id}
                        formType={inviteApplicationForm?.formType || type}
                        onApplicationFormUpdated={setInviteApplicationForm}
                        logoutComponent={isBrokerSession ? null : (
                            <LogoutWithReason
                                withoutReason
                                form_id={application_id}
                                testId={buildAutomationChildTestId(resolvedTestId, "logout")}
                            />
                        )}
                    />
                </div>
            </div>

            <div className="form-content-body form-content-tabs " data-testid={buildAutomationChildTestId(resolvedTestId, "body")}>
                <div className='form-content-body-header' data-testid={buildAutomationChildTestId(resolvedTestId, "body-header")}>
                    <div className='heading' data-testid={buildAutomationChildTestId(resolvedTestId, "title")}>
                        {t(
                            isAppliedForPrime
                                ? "Business Prime Account Application"
                                : isForEdd
                                    ? `Periodic Review - ${getApplicationTitle(type, applicationType)}`
                                    : getApplicationTitle(type, applicationType)
                        )}
                    </div>
                </div>

                <div className='form-content-body-content' data-testid={buildAutomationChildTestId(resolvedTestId, "content")}>
                    <JourneySteps
                        testId={buildAutomationChildTestId(resolvedTestId, "journey")}
                        isForEdd={isForEdd}
                        isKYCComplete={allKYCComplete}
                        requiredUboIds={requiredUboIds}
                        isKycRequired={effectiveKycRequired}
                        isBrokerSession={isBrokerSession}
                        formType={type}
                        type={[FORM_TYPE.BusinessPlus, FORM_TYPE.BusinessPrime, FORM_TYPE.Business]?.includes(type) ? "horizontal" : "vertical"}
                    />

                    {[
                        FORM_TYPE.BusinessPlus,
                        FORM_TYPE.BusinessPrime,
                        FORM_TYPE.IndividualPlus,
                        FORM_TYPE.IndividualPrime,
                        FORM_TYPE.Business
                    ]?.includes(type) && effectiveKycRequired ?
                        <BusinessPlusKYCStatus
                            testId={buildAutomationChildTestId(resolvedTestId, "kyc-status")}
                            onAllKYCComplete={setAllKYCComplete}
                            applicationId={application_id}
                            formType={type}
                            allKYCComplete={allKYCComplete}
                            isKycRequired={effectiveKycRequired}
                            requiredUboIds={requiredUboIds}
                        /> : null}


                    {/* {type == "Business Plus" && !isPrimeApplicationSubmitted ? <RedirectToBusinessPrime isAppliedForPrime={isAppliedForPrime} /> : null} */}
                </div>
            </div>
        </div>
    )
}

export default ThankYou
